import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertTriangle, Play } from 'lucide-react';
import ExamInterface from './ExamInterface';

interface MockTestEngineProps {
  config: {
    subjects: string[];
    chapters: Record<string, string[]>;
    formats: string[];
    totals: Record<string, number>;
    negativeMarking: boolean;
    duration: number;
  };
  onBack: () => void;
  subjectsData: any[]; // Full subject details from parent context
  isDarkMode?: boolean;
}

export default function MockTestEngine({ config, onBack, subjectsData, isDarkMode }: MockTestEngineProps) {
  const [examState, setExamState] = useState<'idle' | 'loading' | 'active' | 'error'>('idle');
  const [questionsData, setQuestionsData] = useState<Record<string, any[]>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // Auto-trigger API fetch when engine mounts
    handleStartExam();
  }, []);

  const handleStartExam = async () => {
    setExamState('loading');
    setErrorMessage('');

    const payload = {
      subjects: config.subjects,
      chapters: config.chapters,
      limits: config.totals,
      settings: {
        examType: config.formats[0] || 'mcq',
        negativeMarking: config.negativeMarking,
        totalTime: config.duration,
      }
    };

    console.log('[MockTestEngine] Dispatching generate payload:', payload);

    const shAuth = (window as any).shAuth;
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (shAuth?.rest_nonce) {
      requestHeaders['X-WP-Nonce'] = shAuth.rest_nonce;
    }

    try {
      const response = await fetch('/wp-json/shuddhoshikha/v1/mock-test/generate', {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Server returned status ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setQuestionsData(result.data);
        setExamState('active');
      } else {
        throw new Error(result.message || 'Failed to retrieve mock test questions.');
      }
    } catch (error: any) {
      console.error('[MockTestEngine] Error generating mock test:', error);
      setErrorMessage(error.message || 'নেটওয়ার্ক সংযোগে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
      setExamState('error');
    }
  };

  if (examState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-32 space-y-6 bg-white dark:bg-brand-dark-bg border border-gray-100 dark:border-white/5 rounded-3xl min-h-[500px]">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
        <div className="text-center space-y-2">
          <h4 className="font-bangla text-lg font-bold text-slate-900 dark:text-white">প্রশ্নপত্র তৈরি হচ্ছে...</h4>
          <p className="font-bangla text-sm text-gray-500 dark:text-slate-400">অনুগ্রহ করে অপেক্ষা করুন, প্রশ্নপত্র সেটআপ হচ্ছে।</p>
        </div>
      </div>
    );
  }

  if (examState === 'error') {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-brand-dark-bg rounded-3xl min-h-[400px] border border-red-500/20 text-center space-y-6">
        <AlertTriangle className="w-16 h-16 text-red-500" />
        <div className="space-y-2">
          <h4 className="font-bangla text-lg font-bold text-slate-900 dark:text-white">ত্রুটি ঘটেছে</h4>
          <p className="font-bangla text-sm text-gray-500 dark:text-slate-400">{errorMessage}</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={onBack}
            className="px-6 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bangla text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            ফিরে যান
          </button>
          <button
            onClick={handleStartExam}
            className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bangla text-sm hover:bg-blue-500 transition-colors"
          >
            আবার চেষ্টা করুন
          </button>
        </div>
      </div>
    );
  }

  if (examState === 'active') {
    return (
      <ExamInterface
        questionsBySubject={questionsData}
        subjectsData={subjectsData}
        duration={config.duration}
        negativeMarking={config.negativeMarking}
        onCancel={onBack}
        isDarkMode={isDarkMode}
        title="মক পরীক্ষা"
      />
    );
  }

  return null;
}
